/* eslint-disable no-console */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { publicRoutes } from 'routes';
import Auth from 'modules/Auth';
import { dataDogLogExternalPublisherId } from 'modules/DataDog';
import Local from 'modules/Local';
import { changeEnvironmentId } from 'store/environment/reducer';

const INTENDED_URL_KEY = 'intendedUrl';
const TARGET_ENV_PARAM = 'targetEnv';

const isPreservableUrl = (url: string): boolean => url !== '/' && url !== '/landing';

export const useApp = (): { isAuthenticated: boolean; isAuthChecked: boolean } => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(null!);
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { markets } = Auth.getData();
  const environmentId = Local.getEnvironmentId();

  const isAuthCallback = location.pathname.includes('auth-callback');

  const isInPublicRoute = useMemo(
    () =>
      publicRoutes.some((route) => {
        const routeRegex = new RegExp(`^${route.path.replace(/:\w+/g, '[^/]+')}$`);
        return routeRegex.test(location.pathname);
      }),
    [location.pathname],
  );

  const returnTo = location.pathname + location.search;

  const handleTargetEnvBypass = (effectiveUrl: string): boolean => {
    if (!effectiveUrl) return false;

    const parsed = new URL(effectiveUrl, window.location.origin);
    const targetEnv = parsed.searchParams.get(TARGET_ENV_PARAM);

    if (!targetEnv) return false;

    parsed.searchParams.delete(TARGET_ENV_PARAM);
    const cleanPath = parsed.pathname + (parsed.search || '');

    if (!markets.includes(targetEnv)) {
      navigate(cleanPath, { replace: true });
      return false;
    }

    Local.setEnvironmentId(targetEnv);
    dispatch(changeEnvironmentId(targetEnv));
    sessionStorage.removeItem(INTENDED_URL_KEY);
    navigate(cleanPath, { replace: true });
    return true;
  };

  const handleMissingEnvironment = (): void => {
    const currentPath = location.pathname + location.search;
    const storedUrl = sessionStorage.getItem(INTENDED_URL_KEY) || '';
    const effectiveUrl = currentPath !== '/landing' && currentPath !== '/' ? currentPath : storedUrl;

    if (handleTargetEnvBypass(effectiveUrl)) return;

    if (isPreservableUrl(currentPath)) {
      sessionStorage.setItem(INTENDED_URL_KEY, currentPath);
    }
    navigate('/landing');
  };

  const routeAuthenticatedUser = (): void => {
    if (markets.length === 0) {
      navigate('/accessdenied');
      return;
    }

    if (environmentId) {
      const currentUrl = location.pathname + location.search;
      handleTargetEnvBypass(currentUrl);
      return;
    }

    handleMissingEnvironment();
  };

  const checkAuth = async (signal: { cancelled: boolean }): Promise<void> => {
    try {
      const authenticated = await Auth.isAuthenticated();

      if (signal.cancelled) return;

      if (!authenticated && !isInPublicRoute) {
        if (isPreservableUrl(returnTo)) {
          sessionStorage.setItem(INTENDED_URL_KEY, returnTo);
        }
        await Auth.redirectToUniversalLoginPage(returnTo);
        return;
      }

      setIsAuthenticated(authenticated);
      setIsAuthChecked(true);
      dataDogLogExternalPublisherId();

      if (authenticated && !isInPublicRoute) {
        routeAuthenticatedUser();
      }
    } catch (err) {
      if (signal.cancelled) return;
      console.error(`Authentication flow error: ${err}`);

      if (isInPublicRoute) {
        setIsAuthenticated(false);
        setIsAuthChecked(true);
        return;
      }

      if (isPreservableUrl(returnTo)) {
        sessionStorage.setItem(INTENDED_URL_KEY, returnTo);
      }
      await Auth.redirectToUniversalLoginPage(returnTo);
    }
  };

  useEffect(() => {
    if (isAuthCallback) return () => {};

    const signal = { cancelled: false };

    setIsAuthChecked(false);

    checkAuth(signal);

    return () => {
      signal.cancelled = true;
    };
  }, [isInPublicRoute, isAuthCallback, environmentId, location.pathname, location.search]);

  return { isAuthenticated, isAuthChecked };
};

/* eslint-enable no-console */
